<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiButton, UiCheckbox, UiDialog, UiText, UiTextarea } from "@shopexperts/shared";
import type { ProjectStatus } from "@/v2Projects/types";
import { getStatusConfig } from "@/v2Projects/utils/display";

type UpdatableProjectStatus = Exclude<ProjectStatus, "quoted">;

export interface ProjectStatusDialogAction {
  targetStatus: UpdatableProjectStatus;
  label: string;
  description: string;
  confirmLabel: string;
  requiresReason?: boolean;
  offersAutoReviewRequests?: boolean;
}

export interface ProjectStatusConfirmPayload {
  reason?: string;
  autoReviewRequestsEnabled?: boolean;
}

const REASON_MAX_LENGTH = 1000;

const props = defineProps<{
  action: ProjectStatusDialogAction | null;
  currentStatus: ProjectStatus;
  isLoading: boolean;
  autoReviewRequestsEnabled?: boolean;
}>();

const open = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  confirm: [payload: ProjectStatusConfirmPayload];
}>();

const reason = ref("");
const autoReviewRequests = ref(true);
const autoReviewRequestsTouched = ref(false);

watch(open, () => {
  reason.value = "";
  autoReviewRequestsTouched.value = false;
  autoReviewRequests.value = props.autoReviewRequestsEnabled ?? true;
}, { immediate: true });

// The project page switch can save while this dialog is open; follow it until the expert picks here.
watch(() => props.autoReviewRequestsEnabled, (enabled) => {
  if (!autoReviewRequestsTouched.value && enabled !== undefined) autoReviewRequests.value = enabled;
});

function setAutoReviewRequests(value: boolean) {
  autoReviewRequestsTouched.value = true;
  autoReviewRequests.value = value;
}

const trimmedReason = computed(() => reason.value.trim());
const isReasonOverLimit = computed(() => reason.value.length > REASON_MAX_LENGTH);
const canConfirm = computed(() => {
  if (!props.action) return false;
  if (!props.action.requiresReason) return true;
  return trimmedReason.value.length > 0 && !isReasonOverLimit.value;
});

function handleConfirm() {
  if (!canConfirm.value) return;
  emit("confirm", {
    reason: props.action?.requiresReason ? trimmedReason.value : undefined,
    autoReviewRequestsEnabled: props.action?.offersAutoReviewRequests ? autoReviewRequests.value : undefined,
  });
}

const currentStatusConfig = computed(() => getStatusConfig(props.currentStatus));
</script>

<template>
  <UiDialog
    v-model="open"
    :title="action?.label ?? ''"
    size="sm"
    @close="open = false"
  >
    <div v-if="action" class="space-y-4">
      <UiText as="p" variant="p-small" class="text-gray-600">
        {{ action.description }}
      </UiText>

      <div class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
        <UiText as="p" variant="caption-normal" class="uppercase tracking-[0.18em] text-gray-500">
          Current project status
        </UiText>
        <div class="mt-2">
          <span
            :class="currentStatusConfig.badgeClass"
            class="inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-semibold leading-4 tracking-wide"
          >
            {{ currentStatusConfig.label }}
          </span>
        </div>
      </div>

      <UiCheckbox
        v-if="action.offersAutoReviewRequests"
        :model-value="autoReviewRequests"
        label="Ask the client for a review automatically"
        description="When the client completes the project, we'll prompt them and email a review request. Turn this off to ask them yourself."
        :disabled="isLoading"
        @update:model-value="setAutoReviewRequests"
      />

      <div v-if="action.requiresReason" class="flex flex-col gap-1">
        <UiText as="label" variant="h6" text="Reason for rejecting" />
        <UiTextarea
          v-model="reason"
          aria-label="Reason for rejecting"
          placeholder="Let us know why you're rejecting this project"
          :rows="4"
          :disabled="isLoading"
          :invalid="isReasonOverLimit"
          :error="isReasonOverLimit ? `Reasons are limited to ${REASON_MAX_LENGTH.toLocaleString()} characters.` : undefined"
          hint="Only the ShopExperts team can see this. The client won't."
        />
      </div>
    </div>

    <template #footer>
      <div class="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
        <UiButton
          variant="outline"
          label="Cancel"
          :disabled="isLoading"
          @click="open = false"
        />
        <UiButton
          variant="primary"
          :label="action?.confirmLabel ?? 'Confirm'"
          :disabled="isLoading || !canConfirm"
          @click="handleConfirm"
        />
      </div>
    </template>
  </UiDialog>
</template>
